import React from 'react';
import PropTypes from 'prop-types';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
	useMediaQuery
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

const useStyles = makeStyles((theme) => ({
	paper: {
		borderRadius: theme.spacing(2),
		overflow: 'hidden',
		backgroundImage: 'none'
	},
	fullScreenPaper: {
		borderRadius: 0
	},
	titleBar: {
		display: 'flex',
		alignItems: 'center',
		minHeight: theme.spacing(7),
		padding: theme.spacing(1, 1, 1, 2.5),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	title: {
		flex: 1,
		minWidth: 0,
		fontWeight: 600,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	closeButton: {
		marginLeft: theme.spacing(1),
		transition: theme.transitions.create(['transform', 'background-color'], {
			duration: theme.transitions.duration.shorter
		}),
		'&:hover': {
			transform: 'rotate(90deg)'
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
			'&:hover': {
				transform: 'none'
			}
		}
	},
	content: {
		display: 'flex',
		padding: '0 !important',
		backgroundColor: '#000'
	},
	playerFrame: {
		position: 'relative',
		width: '100%',
		height: 0,
		paddingTop: '56.25%',
		backgroundColor: '#000'
	},
	playerFrameFullScreen: {
		flex: 1,
		height: '100%',
		paddingTop: 0
	},
	iframe: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		border: 0
	}
}));

export default function TutorialVideoDialog({ open, title, src, onClose }) {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const paperClassName = `${classes.paper} ${fullScreen ? classes.fullScreenPaper : ''}`;
	const playerClassName = `${classes.playerFrame} ${fullScreen ? classes.playerFrameFullScreen : ''}`;

	return (
		<Dialog
			fullWidth
			fullScreen={fullScreen}
			maxWidth="lg"
			open={open && Boolean(src)}
			onClose={onClose}
			aria-labelledby="tutorial-video-dialog-title"
			classes={{ paper: paperClassName }}
			transitionDuration={{ enter: 240, exit: 180 }}
		>
			<DialogTitle disableTypography className={classes.titleBar} id="tutorial-video-dialog-title">
				<Typography variant="h6" className={classes.title}>
					{title}
				</Typography>
				<IconButton className={classes.closeButton} onClick={onClose} aria-label={title}>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent className={classes.content}>
				<div className={playerClassName}>
					{open && src && (
						<iframe
							className={classes.iframe}
							src={src}
							title={title}
							frameBorder="0"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							allowFullScreen
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

TutorialVideoDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	title: PropTypes.string,
	src: PropTypes.string,
	onClose: PropTypes.func.isRequired
};

TutorialVideoDialog.defaultProps = {
	title: '',
	src: ''
};
